import { TagVariant } from '../../components/Tag/Tag';
import i18n from '../../i18n/config';

/**
 * Survey status information used for presentation
 */
export interface SurveyStatusInfo {
  readonly isActive: boolean;
  readonly analyzeStatus: string;
  readonly responseRate: number;
  readonly expectedParticipants: number;
}

/**
 * Response rate calculation result
 */
export interface ResponseRateData {
  readonly rate: number;
  readonly count: number;
  readonly expected: number;
  readonly meetsMinimum: boolean;
}

/**
 * Response rate thresholds based on team size
 *
 * Business rules:
 * - Teams with 1-5 members require 100% response rate
 * - Teams with 6+ members require 75% response rate
 */
export class ResponseRateThresholds {
  private static readonly SMALL_TEAM_MAX_SIZE = 5;
  private static readonly SMALL_TEAM_REQUIRED_RATE = 100;
  private static readonly LARGE_TEAM_REQUIRED_RATE = 75;

  /**
   * Get minimum required response rate based on participant count
   */
  static getMinimumRate(participantCount: number): number {
    if (participantCount <= this.SMALL_TEAM_MAX_SIZE) {
      return this.SMALL_TEAM_REQUIRED_RATE;
    }
    return this.LARGE_TEAM_REQUIRED_RATE;
  }

  /**
   * Check if actual response rate meets the minimum threshold
   */
  static meetsThreshold(
    responseRate: number,
    participantCount: number,
  ): boolean {
    const minimumRate = this.getMinimumRate(participantCount);
    return responseRate >= minimumRate;
  }
}

/**
 * Presenter for custom survey status display
 *
 * Handles all presentation logic for survey cards including:
 * - Status labels (text)
 * - Status variants (tag colors)
 * - Header background colors
 * - Response rate calculations
 */
export class SurveyStatusPresenter {
  /**
   * Get display label for survey status
   *
   * Returns:
   * - "Fullført" if inactive and meets minimum requirements
   * - "Avsluttet" if inactive and doesn't meet requirements
   * - "Analysert" if analyzed
   * - "Pågående" if active
   */
  static getStatusLabel(survey: SurveyStatusInfo): string {
    if (!survey.isActive) {
      const meetsRequirement = ResponseRateThresholds.meetsThreshold(
        survey.responseRate,
        survey.expectedParticipants,
      );
      return meetsRequirement ? i18n.t('customSurveys.status.completed') : i18n.t('customSurveys.status.closed');
    }

    if (survey.analyzeStatus === 'ANALYZED') {
      return i18n.t('customSurveys.status.analyzed');
    }

    return i18n.t('customSurveys.status.ongoing');
  }

  /**
   * Get tag variant for survey status
   *
   * Returns:
   * - 'success' if completed successfully or analyzed
   * - 'warning' if closed without meeting requirements
   * - 'info' if active/ongoing
   */
  static getStatusVariant(survey: SurveyStatusInfo): TagVariant {
    if (!survey.isActive) {
      const meetsRequirement = ResponseRateThresholds.meetsThreshold(
        survey.responseRate,
        survey.expectedParticipants,
      );
      return meetsRequirement ? 'success' : 'warning';
    }

    if (survey.analyzeStatus === 'ANALYZED') {
      return 'success';
    }

    return 'info';
  }

  /**
   * Get header background color based on survey status
   *
   * Returns CSS variable names for consistent theming
   */
  static getHeaderBackground(survey: SurveyStatusInfo): string {
    if (!survey.isActive) {
      const meetsRequirement = ResponseRateThresholds.meetsThreshold(
        survey.responseRate,
        survey.expectedParticipants,
      );
      return meetsRequirement
        ? 'var(--success-light)'
        : 'var(--warning-light)';
    }

    if (survey.analyzeStatus === 'ANALYZED') {
      return 'var(--success-light)';
    }

    return 'var(--survey-card-header-info)';
  }

  /**
   * Calculate response rate information
   */
  static getResponseRateInfo(
    responseCount: number,
    expectedParticipants: number,
  ): ResponseRateData {
    const rate =
      expectedParticipants > 0
        ? (responseCount / expectedParticipants) * 100
        : 0;

    const meetsMinimum = ResponseRateThresholds.meetsThreshold(
      rate,
      expectedParticipants,
    );

    return {
      rate,
      count: responseCount,
      expected: expectedParticipants,
      meetsMinimum,
    };
  }
}

/**
 * Calculator for survey deadline dates
 */
export class SurveyDeadlineCalculator {
  /**
   * Calculate deadline date based on creation date and days
   */
  static calculateDeadline(createdAt: string, deadlineInDays: number): Date {
    const date = new Date(createdAt);
    date.setDate(date.getDate() + deadlineInDays);
    return date;
  }
}
